import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';

import SoftChip from '../../../components/dashboard/SoftChip';
import { BusinessStatusChip, CompanyTypeChip } from '../../../components/dashboard/BusinessChips';
import { formatTaxId } from '../../../utils/formatters';

export const CARD_WIDTH = 248;

const formatLocation = (business) => [business?.city, business?.state].filter(Boolean).join(' / ');

function CompanyLines({ legalName, taxId, country, location }) {
  return (
    <>
      {legalName && (
        <Typography variant="body2" color="text.secondary" noWrap title={legalName}>
          {legalName}
        </Typography>
      )}
      {taxId && (
        <Typography variant="caption" color="text.secondary">
          {formatTaxId(taxId, country)}
        </Typography>
      )}
      {location && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
          <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" noWrap>
            {location}
          </Typography>
        </Stack>
      )}
    </>
  );
}

// Cards dentro do quadro do organograma (Card de elevação 1): elevação 2 para se
// destacarem dele também no dark, onde a elevação clareia o fundo.
const CHART_CARD_ELEVATION = 2;

export function StoreCard({ t, business }) {
  return (
    <Card elevation={CHART_CARD_ELEVATION} sx={{ width: CARD_WIDTH, opacity: business.is_deleted ? 0.6 : 1 }}>
      <CardActionArea component={RouterLink} to={`/businesses/${business.uuid}`}>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle2" noWrap title={business.display_name}>
              {business.display_name}
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              <CompanyTypeChip t={t} companyType={business.company_type} />
              <BusinessStatusChip t={t} business={business} />
            </Stack>
            <CompanyLines
              legalName={business.legal_name}
              taxId={business.tax_id}
              country={business.country}
              location={formatLocation(business)}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// The customer (group). When it is also a store (`rootStore` shares its
// persona), the store is folded into this card instead of a child card.
export function RootCard({ t, customer, rootStore, storesCount, linkToCustomer }) {
  const persona = customer.persona ?? {};
  const name = linkToCustomer ? (
    <Link component={RouterLink} to={`/customers/${customer.uuid}`} underline="hover" color="inherit">
      {customer.display_name}
    </Link>
  ) : (
    customer.display_name
  );

  return (
    <Card
      elevation={CHART_CARD_ELEVATION}
      sx={{
        width: CARD_WIDTH + 32,
        // Destaque do grupo: borda em primary.
        border: 2,
        borderColor: 'primary.main',
        bgcolor: (theme) => theme.alpha((theme.vars || theme).palette.primary.main, 0.04),
      }}
    >
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="h6" noWrap title={customer.display_name}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('organization.storesCount', { count: storesCount })}
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            <SoftChip size="small" color="primary" label={t('organization.group')} />
            {rootStore && (
              <Chip
                size="small"
                variant="outlined"
                color="primary"
                icon={<StoreOutlinedIcon />}
                label={t('organization.alsoStore')}
                title={rootStore.display_name}
                component={RouterLink}
                to={`/businesses/${rootStore.uuid}`}
                clickable
              />
            )}
            {rootStore?.is_deleted && <BusinessStatusChip t={t} business={rootStore} />}
          </Stack>
          <CompanyLines
            legalName={persona.legal_name}
            taxId={persona.tax_id}
            country={persona.country}
            location={formatLocation(rootStore)}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
